import { lazy, Suspense } from 'react';
import { createBrowserRouter, RouterProvider, Navigate } from 'react-router-dom';
import { ErrorBoundary } from './components/ErrorBoundary';
import { RouteErrorFallback } from './components/RouteErrorFallback';
import { AppShell } from './layouts/AppShell';
import { P } from './design-system/palette';
import { useBootstrap } from './hooks/useBootstrap';

const WelcomeScreen = lazy(() => import('./pages/onboarding/WelcomeScreen').then(m => ({ default: m.WelcomeScreen })));
const FoyerChoiceScreen = lazy(() => import('./pages/onboarding/WelcomeScreen').then(m => ({ default: m.FoyerChoiceScreen })));
const ConfigScreen = lazy(() => import('./pages/onboarding/ConfigScreen').then(m => ({ default: m.ConfigScreen })));
const SignupScreen = lazy(() => import('./pages/auth/SignupScreen').then(m => ({ default: m.SignupScreen })));
const LoginScreen = lazy(() => import('./pages/auth/LoginScreen').then(m => ({ default: m.LoginScreen })));
const ForgotPasswordScreen = lazy(() => import('./pages/auth/ForgotPasswordScreen').then(m => ({ default: m.ForgotPasswordScreen })));
const ResetPasswordScreen = lazy(() => import('./pages/auth/ResetPasswordScreen').then(m => ({ default: m.ResetPasswordScreen })));
const JoinFoyerScreen = lazy(() => import('./pages/auth/JoinFoyerScreen').then(m => ({ default: m.JoinFoyerScreen })));
const BudgetPage = lazy(() => import('./pages/budget/BudgetPage').then(m => ({ default: m.BudgetPage })));
const BudgetView = lazy(() => import('./pages/budget/BudgetView').then(m => ({ default: m.BudgetView })));
const ScenariosListView = lazy(() => import('./pages/budget/ScenariosListView').then(m => ({ default: m.ScenariosListView })));
const ScenarioView = lazy(() => import('./pages/budget/ScenarioView').then(m => ({ default: m.ScenarioView })));
const ComparisonView = lazy(() => import('./pages/budget/ComparisonView').then(m => ({ default: m.ComparisonView })));
const HistoriqueView = lazy(() => import('./pages/budget/HistoriqueView').then(m => ({ default: m.HistoriqueView })));
const SnapshotView = lazy(() => import('./pages/budget/SnapshotView').then(m => ({ default: m.SnapshotView })));
const DashboardPage = lazy(() => import('./pages/dashboard/DashboardPage').then(m => ({ default: m.DashboardPage })));
const PatrimoinePage = lazy(() => import('./pages/patrimoine/PatrimoinePage').then(m => ({ default: m.PatrimoinePage })));
const ObjectifsPage = lazy(() => import('./pages/objectifs/ObjectifsPage').then(m => ({ default: m.ObjectifsPage })));
const ObjectifDetail = lazy(() => import('./pages/objectifs/ObjectifDetail').then(m => ({ default: m.ObjectifDetail })));
const ObjectifCreation = lazy(() => import('./pages/objectifs/ObjectifCreation').then(m => ({ default: m.ObjectifCreation })));
const ParametresPage = lazy(() => import('./pages/parametres/ParametresPage').then(m => ({ default: m.ParametresPage })));

const router = createBrowserRouter([
  {
    // Route racine sans path : porte l'errorElement global (react-router
    // n'utilise PAS ErrorBoundary pour les erreurs de rendu de ses routes).
    errorElement: <RouteErrorFallback />,
    children: [
      { path: '/welcome', element: <WelcomeScreen /> },
      { path: '/signup', element: <SignupScreen /> },
      { path: '/login', element: <LoginScreen /> },
      { path: '/forgot-password', element: <ForgotPasswordScreen /> },
      { path: '/reset-password', element: <ResetPasswordScreen /> },
      { path: '/choix-foyer', element: <FoyerChoiceScreen /> },
      { path: '/rejoindre', element: <JoinFoyerScreen /> },
      { path: '/configuration', element: <ConfigScreen /> },

      {
        path: '/',
        element: <AppShell />,
        children: [
          { index: true, element: <Navigate to="/dashboard" /> },
          { path: 'dashboard', element: <DashboardPage /> },
          {
            path: 'budget',
            element: <BudgetPage />,
            children: [
              { index: true, element: <BudgetView /> },
              { path: 'historique', element: <HistoriqueView /> },
              { path: 'historique/:snapshotId', element: <SnapshotView /> },
              { path: 'scenarios', element: <ScenariosListView /> },
              { path: 'scenarios/:scenarioId', element: <ScenarioView /> },
              { path: 'comparer/:idA/:idB', element: <ComparisonView /> },
            ],
          },
          { path: 'patrimoine', element: <PatrimoinePage /> },
          { path: 'objectifs', element: <ObjectifsPage /> },
          { path: 'objectifs/nouveau', element: <ObjectifCreation /> },
          { path: 'objectifs/:id', element: <ObjectifDetail /> },
          { path: 'parametres', element: <ParametresPage /> },
        ],
      },
    ],
  },
]);

function LoadingScreen() {
  return (
    <div style={{
      position: 'fixed', inset: 0,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: P.s0, color: P.t2,
      fontSize: 14, letterSpacing: 0.2,
    }}>
      CashPulse
    </div>
  );
}

function AppContent() {
  const { ready } = useBootstrap();
  if (!ready) return <LoadingScreen />;
  return <RouterProvider router={router} />;
}

export function App() {
  return (
    <ErrorBoundary>
      <Suspense fallback={<LoadingScreen />}>
        <AppContent />
      </Suspense>
    </ErrorBoundary>
  );
}
